import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAvailableDataSources, getUserPreferences, AvailableDataSources, UserPreferences } from '../api/dataSourceApi';
import { useAuth } from '../components/Auth/AuthProvider';
import { debugLog } from '../utils/debugLogger';

interface DataContextValue {
  availableDataSources: AvailableDataSources | null;
  userPreferences: UserPreferences | null;
  // Note: 'models' is kept for backward compatibility but is always empty
  // Models are now loaded via bootstrap in AppStateProvider
  models: string[];
  isLoading: boolean;
  error: string | null;
  refreshUserPreferences: () => Promise<void>;
  refreshAvailableDataSources: () => Promise<void>;
}

const DataContext = createContext<DataContextValue | null>(null);

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [availableDataSources, setAvailableDataSources] = useState<AvailableDataSources | null>(null);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  // Models are loaded via AppStateProvider bootstrap - keep empty array for backward compat
  const models: string[] = [];
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedData, setHasLoadedData] = useState(false);

  const loadAllData = async () => {
    // Don't load data if not authenticated yet
    if (!isAuthenticated) {
      debugLog('[DataContext] Skipping data load - user not authenticated');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      debugLog('[DataContext] Loading data sources and preferences in parallel...');
      
      // Load data sources and preferences in parallel
      // Note: Models are loaded via bootstrap in AppStateProvider, not here
      const [dataSources, preferences] = await Promise.allSettled([
        getAvailableDataSources(),
        getUserPreferences()
      ]);
      
      if (dataSources.status === 'fulfilled') {
        setAvailableDataSources(dataSources.value);
        debugLog('[DataContext] Data sources loaded');
      } else {
        console.error('Failed to load data sources:', dataSources.reason);
      }
      
      if (preferences.status === 'fulfilled') {
        setUserPreferences(preferences.value);
        debugLog('[DataContext] User preferences loaded');
      } else {
        console.error('Failed to load user preferences:', preferences.reason);
      }
      
      setHasLoadedData(true);
      debugLog('[DataContext] Data loaded successfully');
    } catch (err) {
      console.error('[DataContext] Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const refreshUserPreferences = async () => {
    try {
      const preferences = await getUserPreferences();
      setUserPreferences(preferences);
    } catch (err) {
      console.error('[DataContext] Error refreshing user preferences:', err);
    }
  };

  const refreshAvailableDataSources = async () => {
    try {
      debugLog('[DataContext] Refreshing available data sources (force refresh)...');
      // Force refresh to bypass cache (e.g., after creating a Research Scope)
      const dataSources = await getAvailableDataSources(true);
      setAvailableDataSources(dataSources);
      debugLog('[DataContext] Data sources refreshed');
    } catch (err) {
      console.error('[DataContext] Error refreshing data sources:', err);
    }
  };

  // Only load data when user becomes authenticated and data hasn't been loaded yet
  useEffect(() => {
    // Wait for auth to finish loading before doing anything
    if (authLoading) {
      debugLog('[DataContext] Waiting for auth to complete...');
      return;
    }
    
    if (isAuthenticated && !hasLoadedData) {
      debugLog('[DataContext] User authenticated, loading data...');
      loadAllData();
    } else if (!isAuthenticated) {
      // User not authenticated or logged out, reset state
      debugLog('[DataContext] User not authenticated, skipping data load');
      setIsLoading(false);
      setHasLoadedData(false);
    }
  }, [isAuthenticated, authLoading, hasLoadedData]);

  const value: DataContextValue = {
    availableDataSources,
    userPreferences,
    models,
    isLoading,
    error,
    refreshUserPreferences,
    refreshAvailableDataSources
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};